import React, { useState } from 'react';

// "How the fit score works" — the trust panel for the Candidate Graph.
//
// Everything numeric here comes from the `model` object the API ships with each
// page, which is read straight from the active score.model row. Nothing about
// the algebra is written in this file. That is deliberate and load-bearing: a
// guide maintained separately from the algebra is correct on the day it ships
// and wrong the first time a weight moves, and a confidently wrong explanation
// does more damage to trust than no explanation at all.
//
// Follows DataQualityExplainabilityPanel: collapsed by default, one disclosure
// button, same slate panel — a recruiter who wants the ranking does not have to
// read the methodology, and one who doubts it can.

const BUCKET_DOT = {
  call_now: 'bg-emerald-500',
  warm: 'bg-amber-500',
  track: 'bg-sky-500',
  low: 'bg-gray-400',
};

function range(b) {
  if (b.max < 0) return `${b.min} and above`;
  if (b.min === 0) return `below ${b.max + 1}`;
  return `${b.min}–${b.max}`;
}

export default function FitScoreExplainer({
  model,
  title = 'How the fit score works',
  noun = 'candidate',
  intro,
}) {
  const [expanded, setExpanded] = useState(false);
  if (!model || !model.factors || model.factors.length === 0) return null;

  const factors = model.factors;
  const multiplier = model.multiplier || {};
  const buckets = model.buckets || [];
  const eligibility = model.eligibility || {};

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden mb-6">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-100/80 transition-colors"
        aria-expanded={expanded}
        data-testid="fit-explainer-toggle"
      >
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <span className="text-sm text-[#007A6B] font-medium shrink-0">
          {expanded ? 'Hide' : 'Show'}
        </span>
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-200 text-sm text-gray-600" data-testid="fit-explainer">
          {intro ? (
            <p className="pt-4 leading-relaxed max-w-3xl">{intro}</p>
          ) : (
          <p className="pt-4 leading-relaxed max-w-3xl">
            A fit score is built from{' '}
            <strong className="font-medium text-gray-800">evidence you already hold</strong> — your
            ATS records of who worked where, who you know, and who you have spoken to. It is not a
            judgement about the person. Four factors add up to at most{' '}
            <strong className="font-medium text-gray-800">{model.max_base}</strong>, then reachability
            scales the total.
          </p>
          )}

          {/* Factors */}
          <section className="mt-5">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">What earns points</h4>
            <ul className="space-y-3" data-testid="explainer-factors">
              {factors.map((f) => (
                <li key={f.name}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium text-gray-800">{f.label}</span>
                    <span className="text-gray-500 shrink-0 tabular-nums">
                      up to {f.max} pts · {f.share_pct}%
                    </span>
                  </div>
                  {f.why && <p className="text-gray-600 leading-relaxed mt-0.5">{f.why}</p>}
                  <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1.5">
                    <div className="bg-[#1B3A6B] h-1.5 rounded-full" style={{ width: `${f.share_pct}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Multiplier */}
          <section className="mt-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              Then: {multiplier.label || 'Reachability'}
            </h4>
            {multiplier.why && (
              <p className="leading-relaxed max-w-3xl mb-3">
                {multiplier.why.charAt(0).toUpperCase() + multiplier.why.slice(1)}. It{' '}
                <strong className="font-medium text-gray-800">scales</strong> the factors above
                rather than adding to them — it can only ever hold a score back, never create one.
              </p>
            )}
            <ul className="space-y-1.5" data-testid="explainer-reachability">
              {(multiplier.bands || []).map((b) => (
                <li key={b.label} className="flex items-baseline justify-between gap-3 max-w-md">
                  <span>{b.label}</span>
                  <span className="font-medium text-gray-800 tabular-nums shrink-0">
                    ×{b.multiplier.toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* Buckets */}
          <section className="mt-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">What the bands mean</h4>
            <ul className="space-y-2.5" data-testid="explainer-buckets">
              {buckets.map((b) => (
                <li key={b.key} className="flex items-start gap-2.5">
                  <span
                    className={`inline-block w-3 h-3 rounded-sm flex-none mt-1 ${BUCKET_DOT[b.key] || 'bg-gray-400'}`}
                  />
                  <span className="leading-relaxed">
                    <span className="font-medium text-gray-800">{b.label}</span>{' '}
                    <span className="text-gray-500 tabular-nums">({range(b)})</span> — {b.why}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* Who is missing */}
          <section className="mt-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Who is not on this list</h4>
            <p className="leading-relaxed max-w-3xl">
              {`A ${noun} appears only if at least one of these is true:`}
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside" data-testid="explainer-eligibility">
              {(eligibility.requires_any_of || []).map((rule) => (
                <li key={rule} className="leading-relaxed">
                  {rule.replace(/^[a-z_]+:\s*/, '')}
                </li>
              ))}
            </ul>
            <p className="leading-relaxed max-w-3xl mt-2">
              {`A ${noun} with none of them is left out rather than scored zero`} — a zero would claim we
              looked and found them wanting, when the truth is we know nothing about them you could
              act on. Contact details alone do not qualify someone.
            </p>
          </section>

          <p className="mt-6 pt-4 border-t border-slate-200 text-xs text-gray-500">
            Model <span className="font-mono">{model.version}</span>. Every number above is read from
            the scoring model itself, so this guide cannot drift from the ranking it describes.
          </p>
        </div>
      )}
    </div>
  );
}
